"use client";

import { Block } from "@contentful/rich-text-types";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import Image from "next/image";

import { useMemo } from "react";

import {
  ICardCarousel,
  IContentSection,
  IContentSectionFields,
  IHeroArticleFields,
  IRecipeSection,
  IText,
} from "../../@types/generated/contentful";
import {
  formatContentfulDate,
  getIdFromString,
  withHttps,
} from "../../utils/helperFunctions";
import CoachAvatar from "../coach-avatar";
import RichTextRenderer from "../content-section/helper/richTextRenderer";
import DocFleckPlusBadge from "../icons/DocFleckPlusBadge.svg";
import IdAnchor from "../id-anchor";
import TableOfContents, { TableOfContentsEntry } from "../table-of-contents";

interface HeroTeaserProps extends IHeroArticleFields {
  contentSections:
    | (IContentSection | ICardCarousel | IRecipeSection)[]
    | undefined;
  tableOfContents?: TableOfContentsEntry[];
  paywall?: "Keine Paywall" | "Logged in" | "Doc Fleck Plus";
}

const HeroTeaserArticle = ({
  roofline,
  headline,
  introText,
  date,
  author,
  image,
  imageCredits,
  imageLabel,
  contentSections,
  coachAvatar,
  hideReadingTime,
  paywall,
  tableOfContents,
}: HeroTeaserProps) => {
  const theme = useTheme();

  const readingTime = useMemo(
    () => getReadingTime(contentSections),
    [contentSections]
  );
  const Circle = (
    <Box
      sx={{
        display: {
          xs: "none",
          sm: "flex",
        },
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="4"
        height="4"
        viewBox="0 0 4 4"
        fill="none"
      >
        <circle cx="2" cy="2" r="1.5" fill="#FF7E8F" />
      </svg>
    </Box>
  );

  return (
    <Box className="hero-teaser-article">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          mb: {
            xs: tableOfContents ? 0 : image ? 5 : 0,
            sm: tableOfContents ? 0 : image ? 10 : 4,
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            textTransform: "uppercase",
            color: theme.palette.corall.main,
          }}
          variant="body1"
        >
          {roofline}
        </Typography>
        <Typography
          variant="h1"
          sx={{
            my: { xs: theme.spacing(1), sm: theme.spacing(2) },
            px: { xs: 0, sm: 9, md: 19 },
          }}
        >
          <IdAnchor id={getIdFromString(headline)} />
          {paywall == "Doc Fleck Plus" && (
            <DocFleckPlusBadge
              style={{
                color: theme.palette.softBlack.dark,
                width: "32px",
                height: "32px",
                marginBottom: "0px",
                marginRight: "10px",
              }}
            />
          )}
          {headline}
        </Typography>
        {introText && (
          <Box
            sx={{
              maxWidth: "792px",
              textAlign: "left",
              mt: 3,
              " span": { mb: 0 },
            }}
          >
            <RichTextRenderer richTextDocument={introText} />
          </Box>
        )}
        {(date || !hideReadingTime || author) && (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: { xs: "unset", sm: "20px" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                textAlign: "center",
                justifyContent: "center",
                alignItems: "center",

                mt: { xs: theme.spacing(2), sm: 0 },
                mb: coachAvatar ? theme.spacing(2) : 0,
                gap: theme.spacing(1),
              }}
            >
              {date && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ textAlign: "center", fontSize: "14px" }}
                  >
                    Aktualisiert am {formatContentfulDate(date)}
                  </Typography>
                  {(author || !hideReadingTime) && Circle}
                </>
              )}

              {!hideReadingTime && (
                <>
                  <Typography
                    variant="body1"
                    sx={{ textAlign: "center", fontSize: "14px" }}
                  >
                    {readingTime} {readingTime > 1 ? "Minuten" : "Minute"}
                  </Typography>
                  {author && Circle}
                </>
              )}

              {author && (
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", fontSize: "14px" }}
                >
                  von {author}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        {coachAvatar && (
          <Box sx={{ textAlign: "left" }}>
            <CoachAvatar coachAvatar={coachAvatar}></CoachAvatar>
          </Box>
        )}
        {tableOfContents && (
          <TableOfContents tableOfContents={tableOfContents} />
        )}
      </Box>
      <Box
        sx={{
          margin: "auto",

          maxWidth: { xs: "100vw", sm: "90vw" },
        }}
      >
        {image && (
          <>
            <Image
              width={image.fields.file.details.image?.width}
              height={image.fields.file.details.image?.height}
              className="fullscreenImage"
              loading="eager"
              src={withHttps(image.fields.file.url)}
              alt={imageLabel || ""}
              sizes={`(max-width: 900px) 100vw,
            (max-width: 1200px) ${66}vw,
            ${1200}px`}
              layout="responsive"
              style={{
                borderRadius: theme.spacing(2),
                objectFit: "contain",
              }}
            />
            <Box
              sx={{
                mt: theme.spacing(1),
                display: "flex",
                justifyContent: "space-between",
                gap: 3,
              }}
            >
              <Typography variant="caption">{imageLabel}</Typography>
              <Typography sx={{ whiteSpace: "nowrap" }} variant="caption">
                © {imageCredits}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default HeroTeaserArticle;

function getReadingTime(
  contentSections:
    | (IContentSection | ICardCarousel | IRecipeSection)[]
    | undefined
) {
  let totalWords = 0;
  contentSections?.forEach((cs) => {
    if (cs.sys.contentType.sys.id === "contentSection") {
      (cs.fields as IContentSectionFields).sectionItems?.forEach((item) => {
        if (item.sys.contentType.sys.id == "text") {
          const textItem = item as IText;
          textItem.fields.content.content.forEach((contentEntry) => {
            totalWords += getNodeWordcount(contentEntry.content);
          });
        }
      });
    }
  });
  return Math.ceil(totalWords / 238); //238 words per minute
}

function getNodeWordcount(
  content: (
    | Block
    | import("@contentful/rich-text-types").Inline
    | import("@contentful/rich-text-types").Text
  )[]
) {
  let sum = 0;
  content.forEach((contentEntry) => {
    if (contentEntry.nodeType === "text") {
      sum += contentEntry.value.split(" ").length;
    } else {
      sum += getNodeWordcount(contentEntry.content);
    }
  });
  return sum;
}
